// src/utils/transcript.ts
//
// Parse Story 3.6's rendered, role-labeled call transcript
// (yak_calls.transcript) back into speaker-labeled turns for display. The
// stored text is one artifact for display + audit; this is a pure, tolerant
// re-split — it NEVER crashes on a transcript shape drift (an unlabeled or blank
// transcript falls back to a single unlabeled block).
//
// 3.6 renders turns as `Speaker: text`, one turn per line (a turn's own text may
// wrap, but 3.6 emits one line per turn). We treat a leading `Label:` prefix as
// the speaker; lines without one attach to the previous turn (a wrapped
// continuation) or, if there is no previous turn, become an unlabeled block.

export interface TranscriptTurn {
  // The speaker label (e.g. "Agent", "Caller"); null for an unlabeled block.
  speaker: string | null
  text: string
}

// The default collapsed height, in turns (AC 2: "collapsed to ~6 lines").
export const TRANSCRIPT_PREVIEW_TURNS = 6

// A leading label is a short run of word characters/spaces before the first
// colon — kept deliberately strict so a colon inside a sentence ("Note: ...")
// mid-turn isn't mistaken for a speaker. Labels are <= 40 chars, letters/spaces.
const LABEL_RE = /^([A-Za-z][A-Za-z .'-]{0,39}):\s*(.*)$/

export function parseTranscript(
  text: string | null | undefined
): TranscriptTurn[] {
  if (text == null) return []
  const trimmed = text.trim()
  if (trimmed === '') return []

  const turns: TranscriptTurn[] = []
  for (const rawLine of trimmed.split('\n')) {
    const line = rawLine.trim()
    if (line === '') continue

    const m = LABEL_RE.exec(line)
    if (m) {
      turns.push({ speaker: m[1].trim(), text: m[2].trim() })
    } else if (turns.length > 0) {
      // Continuation of the previous turn (a wrapped line).
      const prev = turns[turns.length - 1]
      prev.text = prev.text ? `${prev.text} ${line}` : line
    } else {
      // No label anywhere yet — an unlabeled block.
      turns.push({ speaker: null, text: line })
    }
  }

  return turns
}
